import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, CircularProgress } from "@mui/material";
import InputMask from "react-input-mask";
import { useDispatch } from "react-redux";
import logoImage from "../../image/logo.jpeg";
import firebase from "../../config/firebase";
import shipperService from "../../service/shipper.service";
import authApi from "../../service/modules/auth.api";
import "./cadastro-transportadora.css";

require("firebase/auth");

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PASSWORD_LENGTH = 6;

const INVITE_ERROR_MESSAGE = {
  invalid: "Convite inválido.",
  not_found: "Convite inválido. Este embarcador não foi encontrado.",
  not_embarcador: "Convite inválido. Este link não pertence a um embarcador.",
  error: "Não foi possível validar o convite. Tente novamente.",
};

const EMAIL_IN_USE_MESSAGE =
  "Este e-mail já tem login na Fortio. Use outro e-mail para cadastrar esta empresa, ou entre com esse login se você já é essa transportadora.";

const CARRIER_ALREADY_EXISTS_MESSAGE =
  "Esta transportadora já está na Fortio. Entre com o login existente.";

function getInviteErrorMessage(reason) {
  return INVITE_ERROR_MESSAGE[reason] || INVITE_ERROR_MESSAGE.invalid;
}

function getApiErrorMessage(error) {
  const data = error?.response?.data;
  const code = String(data?.code || "");
  const message = String(data?.message || "").trim();

  if (code === "EMAIL_ALREADY_IN_USE") return EMAIL_IN_USE_MESSAGE;
  if (code === "CARRIER_ALREADY_EXISTS") return CARRIER_ALREADY_EXISTS_MESSAGE;
  if (code === "INVITE_INVALID" || code === "NOT_EMBARCADOR") {
    return message || getInviteErrorMessage("invalid");
  }
  if (code === "CNPJ_INVALID") return "Informe um CNPJ válido.";
  if (code === "WHATSAPP_INVALID") return "Informe um WhatsApp válido.";
  if (code === "WEAK_PASSWORD") {
    return `A senha deve ter pelo menos ${MIN_PASSWORD_LENGTH} caracteres.`;
  }
  if (message) return message;
  return "Não foi possível concluir o cadastro. Tente novamente.";
}

async function hydrateSessionFromAuth(email) {
  const currentUser = firebase.auth().currentUser;
  const uid = currentUser?.uid;
  if (!uid) {
    throw new Error("Sessão Auth não encontrada após o cadastro.");
  }

  const usersSnap = await firebase
    .firestore()
    .collection("user_web")
    .where("uid", "==", uid)
    .limit(1)
    .get();

  if (usersSnap.empty) {
    throw new Error("Usuário criado, mas o perfil ainda não está disponível.");
  }

  const userDoc = usersSnap.docs[0];
  const userData = userDoc.data() || {};
  const uidShipper = String(userData.uidShipper || "").trim();
  let shipperData = null;

  if (uidShipper) {
    const shipperSnap = await firebase
      .firestore()
      .collection("shipper")
      .doc(uidShipper)
      .get();
    if (shipperSnap.exists) {
      shipperData = { id: shipperSnap.id, ...shipperSnap.data() };
    }
  }

  return {
    usuarioEmail: email,
    user: { id: userDoc.id, ...userData },
    shipper: shipperData,
  };
}

function CampaignCarrierSignupPage() {
  const { embarcadorUid } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loadingInvite, setLoadingInvite] = useState(true);
  const [inviteError, setInviteError] = useState("");
  const [embarcador, setEmbarcador] = useState(null);

  const [name, setName] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signupResult, setSignupResult] = useState(null);

  useEffect(() => {
    let mounted = true;

    const inviteUid = String(embarcadorUid || "").trim();
    if (!inviteUid) {
      setInviteError(getInviteErrorMessage("invalid"));
      setEmbarcador(null);
      setLoadingInvite(false);
      return () => {
        mounted = false;
      };
    }

    const loadInvite = async () => {
      setLoadingInvite(true);
      setInviteError("");
      setEmbarcador(null);

      const result = await shipperService.getEmbarcadorForCampaignInvite(
        embarcadorUid,
      );

      if (!mounted) return;

      if (!result.ok) {
        setInviteError(getInviteErrorMessage(result.reason));
        setLoadingInvite(false);
        return;
      }

      setEmbarcador(result.embarcador);
      setLoadingInvite(false);
    };

    loadInvite();
    return () => {
      mounted = false;
    };
  }, [embarcadorUid]);

  const embarcadorDisplayName = useMemo(() => {
    if (!embarcador) return "";
    return embarcador.nameFantasy || embarcador.socialName || "Embarcador Fortio";
  }, [embarcador]);

  const validateForm = () => {
    const trimmedName = String(name || "").trim();
    const cnpjDigits = shipperService.normalizeCnpjDigits(cnpj);
    const whatsappDigits = shipperService.normalizeCnpjDigits(whatsapp);
    const trimmedEmail = String(email || "").trim().toLowerCase();

    if (!trimmedName) return "Informe o nome da transportadora.";
    if (cnpjDigits.length !== 14) return "Informe um CNPJ válido.";
    if (whatsappDigits.length < 10 || whatsappDigits.length > 11) {
      return "Informe um WhatsApp válido.";
    }
    if (!EMAIL_REGEX.test(trimmedEmail)) {
      return "Informe um e-mail válido para o login.";
    }
    if (String(password || "").length < MIN_PASSWORD_LENGTH) {
      return `A senha deve ter pelo menos ${MIN_PASSWORD_LENGTH} caracteres.`;
    }
    if (password !== passwordConfirm) return "A senha e a confirmação não conferem.";
    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationMessage = validateForm();
    if (validationMessage) {
      setFormError(validationMessage);
      return;
    }

    setFormError("");
    setSubmitting(true);

    const trimmedName = String(name || "").trim();
    const trimmedEmail = String(email || "").trim().toLowerCase();

    try {
      const response = await authApi.registerCampaignCarrier({
        embarcadorUid,
        name: trimmedName,
        cnpj,
        whatsapp,
        email: trimmedEmail,
        password,
        loginUrl: `${window.location.origin}/login`,
      });

      const payload = response?.data || {};
      if (payload.outcome === "pending_contact") {
        setSignupResult({
          type: "pending_contact",
          carrierName: payload?.carrier?.name || trimmedName,
        });
        return;
      }
      if (payload.outcome === "linked_existing") {
        setSignupResult({
          type: "linked",
          embarcadorName: payload?.embarcador?.name || embarcadorDisplayName,
          carrierName: payload?.carrier?.name || trimmedName,
        });
        return;
      }

      try {
        await firebase.auth().signInWithEmailAndPassword(trimmedEmail, password);
        const session = await hydrateSessionFromAuth(trimmedEmail);
        dispatch({
          type: "LOG_IN",
          usuarioEmail: session.usuarioEmail,
          user: session.user,
          shipper: session.shipper,
        });
        navigate("/lances");
      } catch (loginError) {
        console.error("Cadastro ok, falha ao entrar automaticamente:", loginError);
        setFormError(
          "Cadastro concluído. Entre com o e-mail e a senha que você cadastrou.",
        );
      }
    } catch (error) {
      const code = String(error?.response?.data?.code || "");
      if (code === "CARRIER_ALREADY_EXISTS") {
        setSignupResult({
          type: "already_registered",
          carrierName: trimmedName,
        });
        return;
      }
      setFormError(getApiErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const updateField = (setter) => (event) => {
    if (formError) setFormError("");
    setter(event.target.value);
  };

  return (
    <div className="campaign-signup-page">
      <div className="campaign-signup-card">
        <div className="campaign-signup-left">
          <h1>Você foi convidado pela empresa <strong>{embarcadorDisplayName}</strong> </h1>
          <p> Crie seu acesso para visualizar fretes e oportunidades.</p>
          <img className="campaign-signup-logo" src={logoImage} alt="Fortio" />
        </div>

        <div className="campaign-signup-right">
          {loadingInvite && (
            <div className="campaign-signup-status">
              <CircularProgress size={28} sx={{ color: "#012443", mb: 1.5 }} />
              <div>Validando convite...</div>
            </div>
          )}

          {!loadingInvite && inviteError ? (
            <>
              <h2>Cadastro</h2>
              <div className="campaign-signup-status campaign-signup-status--error">
                {inviteError}
              </div>
              <div className="campaign-signup-footer">
                Já tem cadastro? <Link to="/login">Entrar</Link>
              </div>
            </>
          ) : null}

          {!loadingInvite && !inviteError && signupResult ? (
            <>
              <h2>Cadastro</h2>
              {signupResult.type === "pending_contact" ? (
                <div className="campaign-signup-success">
                  Obrigado pelo seu cadastro, em instantes entraremos em contato
                  para confirmar alguns dados.
                </div>
              ) : (
                <>
                  <div className="campaign-signup-success">
                    <strong>Esta transportadora já está na Fortio.</strong>
                    {signupResult.type === "linked" ? (
                      <>
                        Vinculamos {signupResult.carrierName} ao embarcador{" "}
                        {signupResult.embarcadorName}. Entre com o login que vocês já
                        usam para ver os fretes em Lances.
                      </>
                    ) : (
                      <>
                        {signupResult.carrierName
                          ? `${signupResult.carrierName} já tem cadastro.`
                          : "Este CNPJ já tem cadastro."}{" "}
                        Entre com o login existente para acessar os Lances.
                      </>
                    )}
                  </div>
                  <Button
                    component={Link}
                    to="/login"
                    variant="contained"
                    fullWidth
                    sx={{
                      mt: 2,
                      py: 1.2,
                      backgroundColor: "#d32f2f",
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: "10px",
                      "&:hover": { backgroundColor: "#b71c1c" },
                    }}
                  >
                    Entrar
                  </Button>
                </>
              )}
            </>
          ) : null}

          {!loadingInvite && !inviteError && !signupResult && embarcador ? (
            <>
              <h2>Cadastro</h2>
              <form className="campaign-signup-form" onSubmit={handleSubmit}>
                <div className="campaign-signup-invite">
                  Você foi convidado por
                  <strong>{embarcadorDisplayName}</strong>
                </div>

                <div className="campaign-signup-field">
                  <label htmlFor="campaign-carrier-name">Nome *</label>
                  <input
                    id="campaign-carrier-name"
                    type="text"
                    value={name}
                    onChange={updateField(setName)}
                    placeholder="Nome da transportadora"
                    autoComplete="organization"
                    disabled={submitting}
                  />
                </div>

                <div className="campaign-signup-field">
                  <label htmlFor="campaign-carrier-cnpj">CNPJ *</label>
                  <InputMask
                    mask="99.999.999/9999-99"
                    value={cnpj}
                    onChange={updateField(setCnpj)}
                    id="campaign-carrier-cnpj"
                    type="text"
                    placeholder="00.000.000/0000-00"
                    inputMode="numeric"
                    disabled={submitting}
                  />
                </div>

                <div className="campaign-signup-field">
                  <label htmlFor="campaign-carrier-whatsapp">WhatsApp *</label>
                  <InputMask
                    mask="(99) 99999-9999"
                    value={whatsapp}
                    onChange={updateField(setWhatsapp)}
                    id="campaign-carrier-whatsapp"
                    type="tel"
                    placeholder="(00) 00000-0000"
                    inputMode="tel"
                    disabled={submitting}
                  />
                </div>

                <div className="campaign-signup-field">
                  <label htmlFor="campaign-carrier-email">Login (e-mail) *</label>
                  <input
                    id="campaign-carrier-email"
                    type="email"
                    value={email}
                    onChange={updateField(setEmail)}
                    placeholder="grace.l@example.com"
                    autoComplete="username"
                    disabled={submitting}
                  />
                </div>

                <div className="campaign-signup-field">
                  <label htmlFor="campaign-carrier-password">Senha *</label>
                  <input
                    id="campaign-carrier-password"
                    type="password"
                    value={password}
                    onChange={updateField(setPassword)}
                    placeholder="Mínimo de 6 caracteres"
                    autoComplete="new-password"
                    disabled={submitting}
                  />
                </div>

                <div className="campaign-signup-field">
                  <label htmlFor="campaign-carrier-password-confirm">
                    Confirmar senha *
                  </label>
                  <input
                    id="campaign-carrier-password-confirm"
                    type="password"
                    value={passwordConfirm}
                    onChange={updateField(setPasswordConfirm)}
                    placeholder="Repita a senha"
                    autoComplete="new-password"
                    disabled={submitting}
                  />
                </div>

                {formError ? (
                  <div className="campaign-signup-error">{formError}</div>
                ) : null}

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={submitting}
                  sx={{
                    mt: 1,
                    py: 1.2,
                    fontSize: "15px",
                    backgroundColor: "#d32f2f",
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: "10px",
                    "&:hover": { backgroundColor: "#b71c1c" },
                  }}
                >
                  {submitting ? (
                    <CircularProgress size={22} sx={{ color: "#ffffff" }} />
                  ) : (
                    "Cadastrar"
                  )}
                </Button>
              </form>

              <div className="campaign-signup-footer">
                Já tem cadastro? <Link to="/login">Entrar</Link>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default CampaignCarrierSignupPage;
